'use client';

import type {
  AiDiscoveryVerificationResultDto,
  AiDiscoveryVerificationRollupDto,
} from '@terra/shared-contracts-agent-execution';
import { Badge, MetaDefault } from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';

type AiDiscoveryProbeVerificationProps = {
  results: AiDiscoveryVerificationResultDto[];
  rollup?: AiDiscoveryVerificationRollupDto | null;
  labels: {
    title: string;
    rollup: string;
    claim: string;
    stimulus: string;
    predictedSignal: string;
    observedResponse: string;
    confirmed: string;
    contradicted: string;
    emptyObserved: string;
    invocation?: string;
    invocationHidden?: string;
    invocationUserVisible?: string;
    invocationUnknown?: string;
    exchange?: string;
  };
};

export function AiDiscoveryProbeVerification({
  results,
  rollup,
  labels,
}: AiDiscoveryProbeVerificationProps) {
  if (!rollup && results.length === 0) {
    return null;
  }

  return (
    <VStack gap={3} alignItems="stretch">
      <MetaDefault>{labels.title}</MetaDefault>
      {rollup ? (
        <MetaDefault color="fg.muted">{labels.rollup}</MetaDefault>
      ) : null}
      {results.map((result) => (
        <Box
          key={`${result.componentType}:${result.componentName}:${result.claim}`}
          borderWidth="1px"
          borderColor="border.muted"
          borderRadius="md"
          p={3}
        >
          <VStack gap={2} alignItems="stretch">
            <Badge variant="outline">
              {result.outcome === 'CONFIRMED'
                ? labels.confirmed
                : labels.contradicted}
              {` · ${result.componentType} · ${result.componentName}`}
            </Badge>
            <Field label={labels.claim} value={result.claim} />
            <Field label={labels.stimulus} value={result.stimulus} />
            <Field
              label={labels.predictedSignal}
              value={result.predictedSignal}
            />
            <Field
              label={labels.observedResponse}
              value={result.observedResponse || labels.emptyObserved}
            />
            {result.invocationVisibility && labels.invocation ? (
              <Field
                label={labels.invocation}
                value={
                  result.invocationVisibility === 'hidden'
                    ? (labels.invocationHidden ?? result.invocationVisibility)
                    : result.invocationVisibility === 'user_visible'
                      ? (labels.invocationUserVisible ??
                        result.invocationVisibility)
                      : (labels.invocationUnknown ??
                        result.invocationVisibility)
                }
              />
            ) : null}
            {result.exchangeToolCallId && labels.exchange ? (
              <Field
                label={labels.exchange}
                value={result.exchangeToolCallId}
              />
            ) : null}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <VStack gap={1} alignItems="stretch">
      <MetaDefault color="fg.muted">{label}</MetaDefault>
      <MetaDefault whiteSpace="pre-wrap">{value}</MetaDefault>
    </VStack>
  );
}
